import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MagnifyingGlass, Minus, Plus, Bus, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { passengerApi } from "@/lib/passengerApi";
import { MajheHeader } from "@/components/majhe/MajheLayout";

export default function QuickBook() {
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [passengers, setPassengers] = useState(1);
  const [buying, setBuying] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    passengerApi.get("/routes")
      .then(({ data }) => setRoutes(data))
      .catch(() => toast.error("Could not load routes"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return routes;
    return routes.filter((r) => r.name.toLowerCase().includes(s) || r.route_id.toLowerCase().includes(s));
  }, [routes, q]);

  const buy = async () => {
    if (!selected) return;
    setBuying(true);
    const [from_stop, to_stop] = selected.name.includes("-")
      ? selected.name.split("-").map((p) => p.trim())
      : [selected.name, selected.name];
    try {
      const { data } = await passengerApi.post("/tickets/buy", {
        route_id: selected.route_id,
        from_stop, to_stop,
        passengers,
      });
      toast.success("Ticket booked!");
      navigate(`/majhe/tickets/${data.ticket_no}`, { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Booking failed");
    } finally {
      setBuying(false);
    }
  };

  if (selected) {
    const total = (selected.fare * passengers).toFixed(0);
    return (
      <div data-testid="majhe-quick-book-confirm">
        <MajheHeader title="Confirm Booking" onBack={() => setSelected(null)}/>
        <div className="px-5 pt-4 space-y-4">
          <div className="bg-white border border-[#EDE8FA] rounded-2xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-[#EDE5FF] text-[#5B2EE5] flex items-center justify-center shrink-0">
                <Bus size={20} weight="fill"/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm text-[#18142A] truncate">{selected.name}</div>
                <div className="text-[11px] text-[#7B7390] mt-0.5">{selected.category} · {selected.route_id}</div>
              </div>
            </div>
          </div>

          <div className="bg-white border border-[#EDE8FA] rounded-2xl p-4 flex items-center justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[#7B7390] font-bold">Passengers</div>
              <div className="text-[12px] text-[#7B7390] mt-0.5">₹{selected.fare} per passenger</div>
            </div>
            <div className="flex items-center gap-3">
              <button data-testid="qty-minus" type="button"
                onClick={() => setPassengers((p) => Math.max(1, p - 1))}
                className="w-9 h-9 rounded-lg bg-[#F5F2FF] text-[#5B2EE5] flex items-center justify-center">
                <Minus size={16} weight="bold"/>
              </button>
              <span data-testid="qty-value" className="font-extrabold text-lg w-6 text-center">{passengers}</span>
              <button data-testid="qty-plus" type="button"
                onClick={() => setPassengers((p) => Math.min(6, p + 1))}
                className="w-9 h-9 rounded-lg bg-[#F5F2FF] text-[#5B2EE5] flex items-center justify-center">
                <Plus size={16} weight="bold"/>
              </button>
            </div>
          </div>

          <div className="bg-[#F5F2FF] border border-[#DCC9FF] rounded-2xl p-4 flex items-center justify-between">
            <span className="text-sm font-bold text-[#18142A]">Total fare</span>
            <span className="text-xl font-extrabold text-[#5B2EE5]">₹{total}</span>
          </div>

          <button data-testid="confirm-buy-btn" onClick={buy} disabled={buying}
            type="button" className="majhe-btn mt-1 disabled:opacity-50">
            <CheckCircle size={18} weight="bold"/> {buying ? "Booking…" : "Confirm & Get QR Ticket"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="majhe-quick-book">
      <MajheHeader title="Buy Ticket" onBack={() => navigate("/majhe")}/>
      <div className="px-5 pt-2">
        <div className="relative">
          <MagnifyingGlass size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#7B7390]"/>
          <input data-testid="route-search-input" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search route, e.g. Margao - Panaji"
            className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-full text-sm font-medium outline-none focus:border-[#5B2EE5] focus:bg-white"/>
        </div>
      </div>

      <div className="px-5 mt-4 space-y-2 pb-8">
        {loading && [0, 1, 2, 3, 4].map((i) => <div key={i} className="majhe-skel h-16"/>)}
        {!loading && filtered.length === 0 && (
          <div className="text-center text-[#7B7390] text-sm py-12">No routes match &quot;{q}&quot;</div>
        )}
        {!loading && filtered.map((r) => (
          <button key={r.route_id} data-testid={`route-row-${r.route_id}`} type="button"
            onClick={() => setSelected(r)}
            className="w-full flex items-center gap-3 p-3.5 bg-white border border-[#EDE8FA] rounded-2xl hover:bg-[#F5F2FF] transition-colors text-left">
            <div className="w-10 h-10 rounded-full bg-[#EDE5FF] text-[#5B2EE5] flex items-center justify-center shrink-0">
              <Bus size={18} weight="fill"/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm text-[#18142A] truncate">{r.name}</div>
              <div className="text-[11px] text-[#7B7390] mt-0.5">{r.category} · {r.route_id}</div>
            </div>
            <div className="text-[13px] font-extrabold text-[#5B2EE5] shrink-0">₹{r.fare}</div>
          </button>
        ))}
      </div>
    </div>
  );
}
